import { mapGetters } from 'vuex'
import GridConfigMixin from '@/host/components/mixins/grid/Config'
import { Queue } from '@/common/model'

/**
 * Mixin for column computation
 */
export default {
  mixins: [GridConfigMixin],
  data() {
    return {
      allLabelFit: true,
      withCloneQueue: new Queue(1)
    }
  },
  computed: {
    ...mapGetters(['isSinglePaymentButton'])
  },
  watch: {
    async isSinglePaymentButton() {
      await this.updateRadioButtonWidth()
    }
  },
  async mounted() {
    await this.updateRadioButtonWidth()
  },
  methods: {
    async updateRadioButtonWidth() {
      // Skip calculation outside of SPB mode or if the value has already
      // Been calculated
      if (!this.isSinglePaymentButton || this.radioBtnWidth > 0) return

      this.radioBtnWidth = await this.withCloneQueued(
        { label: false, width: this.width },
        async $clone => {
          const radioBtn = $clone.querySelector('.kr-smart-form-radio-button')
          return this.isSinglePaymentButton && radioBtn instanceof HTMLElement
            ? radioBtn.offsetWidth
            : 0
        }
      )
      return
    },
    async computeColumns(width, gap) {
      const columns = this.computeColumnsIgnoringLabels(width, gap)

      if (this.labelVisibility === 'show') {
        return await this.capMaxColumnsWithLabels(width, columns)
      } else if (this.labelVisibility === 'auto') {
        this.allLabelFit = await this.everyLabelFitForGivenColumns(
          width,
          columns
        )
      }

      return columns
    },
    computeColumnsIgnoringLabels(width, gap) {
      const maxCols = this.getMaxColumns(width, gap)

      if (this.columns === 'max' || this.columns > maxCols)
        // There cannot be more columns than payment methods available
        return Math.min(maxCols, this.groupMethods)

      return Math.min(this.columns, this.groupMethods)
    },
    getMaxColumns(spaceAvailable, gap) {
      const spacePerCol = this.calculateMinSpacePerColumn(gap)

      return Math.floor(spaceAvailable / spacePerCol)
    },
    calculateMinSpacePerColumn(gap) {
      return parseInt(this.configColMinWidth) + (parseInt(gap) || 0)
    },
    /**
     * Check whether all labels fit for a given number of columns.
     *
     * @param {number} width
     * @param {number} columns
     * @returns {Promise<boolean>}
     * @since KJS-4113
     */
    async everyLabelFitForGivenColumns(width, columns) {
      return await this.withCloneQueued(
        { label: true, width },
        async $clone => {
          $clone.style.gridTemplateColumns = `repeat(${columns}, 1fr)`
          await this.$nextTick()
          return this.everyLabelFitWithinElement($clone)
        }
      )
    },
    /**
     * Given a number of columns initially computed by
     * computeColumnsIgnoringLabels method, we need to check whether every
     * single label can be displayed without overflow.
     * If not, decrease value and test it again.
     * Repeat process until the number of columns would allow it or until we
     * reach the minimum (1).
     *
     * To do this a clone of the button container is made.
     * This allows applying specific styles required for the calculation.
     *
     * @param {number} width Target width
     * @param {number} columns Column number initially computed
     * @returns {number} Max number of columns which enables displaying labels
     *                   for every method.
     * @since KJS-4162
     * @see KJS-4822 | Catch errors and return original value to make it
     *  fault tolerant.
     */
    async capMaxColumnsWithLabels(width, columns) {
      try {
        await this.withCloneQueued({ label: true, width }, async $clone => {
          while (columns > 1) {
            $clone.style.gridTemplateColumns = `repeat(${columns}, 1fr)`
            await this.$nextTick()
            if (this.everyLabelFitWithinElement($clone)) {
              break
            }
            columns -= 1
          }
        })
      } catch (e) {
        console.error(e)
      }
      return columns
    },
    /**
     * For every button (.kr-method-btn) within a given clone of .kr-grid-group,
     * check whether it is large enough for its content:
     * .kr-smart-form-radio-button if present, .kr-icon, <span>, and a spacing (e.g: 10px with Neon theme)
     *
     * @param {HTMLElement} $clone
     * @returns {boolean}
     * @since KJS-4162
     * @see KJS-4817 | Add height check and check label > span instead of label
     * @see KJS-4847 | Add configurable Y padding
     */
    everyLabelFitWithinElement($clone) {
      const getElWidth = (baseEl, selector) =>
        baseEl.querySelector(selector)?.getBoundingClientRect().width ?? 0

      return [...$clone.querySelectorAll('.kr-method-btn__label')].every(
        btnLabel => {
          const $parent = btnLabel.parentElement
          const isNativeLook = $parent.classList.contains(
            'kr-method-btn--native-look'
          )
          const labelWidth = !isNativeLook ? getElWidth(btnLabel, 'span') : 0
          const paddingR = !isNativeLook ? this.configButtonPaddingRight : 0
          // Icons size (use kr-brand-icon for native look) @since KJS-6092
          const iconWidth = getElWidth(
            $parent,
            !isNativeLook ? '.kr-icon' : '.kr-brand-icon'
          )

          const fitsHorizontally =
            $parent.getBoundingClientRect().width >=
            getElWidth($parent, '.kr-smart-form-radio-button') +
              iconWidth +
              labelWidth +
              paddingR

          const fitsVertically =
            btnLabel.offsetHeight + this.configButtonPaddingY <=
            $parent.offsetHeight

          return fitsHorizontally && fitsVertically
        }
      )
    },
    /**
     * Queued version of withClone method.
     * Cannot be executed more than 1 at the time.
     */
    async withCloneQueued(opts, fn) {
      return this.withCloneQueue.push(() => this.withClone(opts, fn))
    },
    /**
     * Create an invisible clone of kr-grid-group and execute the given task.
     * This allow to check, in other methods, to calculate the optimal amount of
     * columns for visible labels or check whether label have enough space for
     * a fix amount of columns.
     *
     * @template T
     * @param {object}  opts Options
     * @param {boolean} opts.label Make clone's labels visible or not
     * @param {nunber}  opts.width Target width for the calculation.
     *  If omitted, the $refs.gridGroup will be used.
     * @param {function($clone: HTMLElement) => Promise<T>} fn Task to execute.
     *    Provided with the create $clone to test.
     * @returns {Promise<T>}
     * @since KJS-4113
     */
    async withClone(opts, fn) {
      const $gridGroup = this.$refs.gridGroup
      if (!$gridGroup) return
      const $clone = $gridGroup.cloneNode(true)

      $clone.setAttribute('data-testid', 'gridGroupClone') // For unit-test
      $clone.style.opacity = '0'
      $clone.style.position = 'absolute'
      if ($clone.style.display === 'none') {
        $clone.style.removeProperty('display')
      }
      // Force every direct child position to static to ensure calculation is
      // done right (e.g: absolute element will not cause an overflow when
      // visually there is not space)
      $clone.querySelectorAll('.kr-method-btn > *').forEach(el => {
        el.style.position = 'static'
      })
      $clone.querySelectorAll('.kr-method-btn__label').forEach(el => {
        const isNativeLook = el.parentElement.classList.contains(
          'kr-method-btn--native-look'
        )
        if (opts.label === true && !isNativeLook) {
          el.classList.remove('kr-method-btn__label--hidden')
        } else {
          el.classList.add('kr-method-btn__label--hidden')
          el.style.removeProperty('position')
        }
        el.style.gridColumnStart = 'auto'
      })

      // in stretch mode this can make a button to occupy more space
      // than just one column breaking the column computation see KJS-5478
      $clone.querySelectorAll('.kr-method-btn').forEach(el => {
        el.style.gridColumnStart = 'auto'
      })

      const w = opts.width ?? $gridGroup.offsetWidth
      $clone.style.width = `${w}px`
      $gridGroup.parentElement.appendChild($clone)

      try {
        return await fn($clone)
      } catch (e) {
        throw e
      } finally {
        $gridGroup.parentElement.removeChild($clone)
      }
    }
  }
}
